import { Picture } from 'omakase/Picture/Picture';
import { Theme } from '@emotion/react';
import { Typography } from 'omakase';
import {
  heroCardImage,
  heroCardRoot,
  heroCardText,
  imageHeightDesktop,
  imageHeightMobile,
} from './heroCardCssDefs';
import React, { FunctionComponent } from 'react';

interface HeroCardImageProps {
  /**
   * Alt text for the image
   */
  alt: string;
  /**
   * src for the image at the desktop scale
   */
  srcDesktop: string;
  /**
   * src for the image at the mobile scale
   */
  srcMobile: string;
}

interface HeroCardNoImageProps {
  /**
   * Alt text for the image
   */
  alt?: undefined;
  /**
   * src for the image at the desktop scale
   */
  srcDesktop?: undefined;
  /**
   * src for the image at the mobile scale
   */
  srcMobile?: undefined;
}

interface HeroCardBaseProps {
  /**
   * the custom color override for the card
   */
  color?: string;
  /**
   * the primary text for the card
   */
  primaryText: string;
  /**
   * the secondary text for the card
   */
  secondaryText: string;
  /**
   * the HTML element to render for the card's primary text
   */
  headingComponent?: React.ElementType;
}

type HeroCardWithImageProps = HeroCardBaseProps & HeroCardImageProps;
type HeroCardWithoutImageProps = HeroCardBaseProps & HeroCardNoImageProps;
export type HeroCardProps = HeroCardWithImageProps | HeroCardWithoutImageProps;

/**
 * HeroCard
 */
const HeroCard: FunctionComponent<HeroCardProps> = ({
  alt,
  color,
  headingComponent = 'p',
  primaryText,
  secondaryText,
  srcDesktop,
  srcMobile,
}) => {
  return (
    <div css={heroCardRoot}>
      <div css={(theme: Theme) => heroCardText(theme, color)}>
        <Typography color="inherit" component={headingComponent} variant="heading02">
          {primaryText}
        </Typography>
        {secondaryText && (
          <Typography color="inherit" variant="body01">
            {secondaryText}
          </Typography>
        )}
      </div>
      {srcMobile && (
        <Picture src={{ xs: srcMobile, md: srcDesktop }} imgProps={{ alt, css: heroCardImage }} />
      )}
    </div>
  );
};

/** Returns the height for a mobile hero card. */
export function computeHeroMobileHeight(
  { spacing }: Theme,
  {
    color,
    srcMobile,
    secondaryText,
  }: { color?: string; srcMobile?: string; secondaryText?: string }
) {
  // We compute the height of spacing + border, typography height, and the height of the image.
  // The typography height is an outgrowth of the 'heading02' and 'body01' typography styles, including
  // font size in rems and line height. It's hard to compute this from first principles, we just
  // hard-code it.
  return spacing(color ? 8 : 7) + (secondaryText ? 52 : 32) + (srcMobile ? imageHeightMobile : 0);
}

/** Returns the height for a desktop hero card. */
export function computeHeroDesktopHeight(
  { spacing }: Theme,
  { srcMobile }: { srcMobile?: string }
) {
  // Computation is done much as it is for mobile, but note that there is no additional
  // border stripe on desktop. The typography height comes from 'legacyDisplay07'<div className="
  // Also, the text is all on one line, whether there is secondary text or not.
  return spacing(7) + 42 + (srcMobile ? imageHeightDesktop : 0);
}

export default HeroCard;
